import { and, collection, getCountFromServer, or, query, where } from "firebase/firestore";
import Failure from "../../../core/Failure";
import { FirestoreCollections, firestore } from "../../../firebase.config";
import dayjs from "dayjs";

export interface MessageStatsCounts {
  timestamp: number;
  smsSentCount: number;
  smsReceivedCount: number;
  whatsAppMessageSentCount: number;
  whatsAppMessageReceivedCount: number;
  callrSmsSentCount: number;
  callrSmsReceivedCount: number;
  messagesNotDeliveredCount: number;
}

async function getMessagesStats(days: number): Promise<MessageStatsCounts[]> {
  try {
    const db = firestore;
    const smsCollection = collection(db, FirestoreCollections.sms);
    const today = dayjs().startOf("day");
    const statsPerDay: MessageStatsCounts[] = [];

    for (let i = 0; i < days; i++) {
      const dayStart = today.subtract(i, "days").toDate().getTime();
      const dayEnd = today.subtract(i - 1, "days").toDate().getTime();
      const timestamp = today.subtract(i, "days").toDate().getTime();

      // Run all queries in parallel
      const [
        smsSentCountSnapshot,
        smsReceivedCountSnapshot,
        whatsAppMessageSentCountSnapshot,
        whatsAppMessageReceivedCountSnapshot,
        callrSmsSentCountSnapshot,
        callrSmsReceivedCountSnapshot,
        messagesNotDeliveredCountSnapshot

      ] = await Promise.all([
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'sakari'),
            where('deliverability', '==', 'delivered'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'sakari'),
            where('deliverability', '==', 'n/a'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'twilio'),
            where('deliverability', '==', 'delivered'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'twilio'),
            where('deliverability', '==', 'n/a'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'callr'),
            where('deliverability', '==', 'delivered'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            where('platform', '==', 'callr'),
            where('deliverability', '==', 'n/a'),
            where('created', '>=', dayStart),
            where('created', '<', dayEnd)
          )
        ),
        getCountFromServer(
          query(smsCollection,
            and(
              where('created', '>=', dayStart),
              where('created', '<', dayEnd),
              or(
                where('deliverability', '==', 'delivery_failed'),
                where('deliverability', '==', 'error')
              )
            ),
          )
        )
      ]);

      statsPerDay.push({
        timestamp: timestamp,
        smsSentCount: smsSentCountSnapshot.data().count ?? 0,
        smsReceivedCount: smsReceivedCountSnapshot.data().count ?? 0,
        whatsAppMessageSentCount: whatsAppMessageSentCountSnapshot.data().count ?? 0,
        whatsAppMessageReceivedCount: whatsAppMessageReceivedCountSnapshot.data().count ?? 0,
        callrSmsSentCount: callrSmsSentCountSnapshot.data().count ?? 0,
        callrSmsReceivedCount: callrSmsReceivedCountSnapshot.data().count ?? 0,
        messagesNotDeliveredCount: messagesNotDeliveredCountSnapshot.data().count ?? 0,
      });
    }

    return statsPerDay.reverse();
  } catch (e) {
    console.log("error: ", e);
    throw new GetMessagesStatsFailure();
  }
}

export class GetMessagesStatsFailure extends Failure {
  constructor(message = "Unknown error", code = "unknown-error") {
    super(message, code);
  }
}

export default getMessagesStats;
